import React, { useEffect, useMemo, useState } from "react";
import API from "../../axiosInstance";

export default function AdminTransactionsPanel() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    API.get("/admin/transactions")
      .then((res) => {
        setTransactions(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("❌ Erreur chargement transactions :", err);
        setTransactions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return transactions;
    return transactions.filter((tx) =>
      String(tx.user_email || "").toLowerCase().includes(q) ||
      String(tx.pack_id || "").includes(q) ||
      String(tx.amount_paid || "").includes(q)
    );
  }, [transactions, search]);

  const total = useMemo(() => {
    return filtered.reduce((sum, tx) => sum + Number(tx.amount_paid || 0), 0);
  }, [filtered]);

  const todayTotal = useMemo(() => {
    const today = new Date().toDateString();
    return transactions.reduce((sum, tx) => {
      if (!tx.purchase_date) return sum;
      return new Date(tx.purchase_date).toDateString() === today
        ? sum + Number(tx.amount_paid || 0)
        : sum;
    }, 0);
  }, [transactions]);

  const formatEuro = (v) =>
    Number(v || 0).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>EVO LIVE ADMIN</div>
          <h1 style={styles.title}>💳 Transactions Coins</h1>
          <p style={styles.subtitle}>
            Achats de packs, montants payés et historique des utilisateurs.
          </p>
        </div>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Total affiché</span>
          <strong style={styles.statValue}>{formatEuro(total)} €</strong>
        </div>

        <div style={styles.statCard}>
          <span style={styles.statLabel}>Transactions</span>
          <strong style={styles.statValue}>{filtered.length}</strong>
        </div>

        <div style={styles.statCard}>
          <span style={styles.statLabel}>Aujourd’hui</span>
          <strong style={styles.statValue}>{formatEuro(todayTotal)} €</strong>
        </div>
      </div>

      <div style={styles.toolbar}>
        <input
          style={styles.search}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par email, pack ou montant..."
        />
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.empty}>Chargement des transactions...</div>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>Aucune transaction trouvée.</div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Utilisateur</th>
                  <th style={styles.th}>Pack</th>
                  <th style={styles.th}>Montant payé</th>
                  <th style={styles.th}>Date achat</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((tx) => (
                  <tr key={tx.id} style={styles.tr}>
                    <td style={styles.tdMuted}>#{tx.id}</td>
                    <td style={styles.tdEmail}>{tx.user_email}</td>
                    <td style={styles.td}>
                      <span style={styles.badge}>Pack #{tx.pack_id}</span>
                    </td>
                    <td style={styles.tdAmount}>
                      {formatEuro(tx.amount_paid)} €
                    </td>
                    <td style={styles.tdMuted}>{formatDate(tx.purchase_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #020617 0%, #07111f 45%, #041d2b 100%)",
    color: "#fff",
    padding: "32px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  kicker: {
    color: "#00f2ff",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "2px",
    marginBottom: "6px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    fontWeight: 900,
    textShadow: "0 0 14px rgba(0,242,255,0.45)",
  },
  subtitle: {
    color: "#8fa2bd",
    marginTop: "8px",
    fontSize: "14px",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "18px",
  },
  statCard: {
    background: "rgba(7, 17, 31, 0.92)",
    border: "1px solid rgba(0,242,255,0.24)",
    borderRadius: "18px",
    padding: "18px",
    boxShadow: "0 0 24px rgba(0,242,255,0.08)",
  },
  statLabel: {
    display: "block",
    color: "#8fa2bd",
    fontSize: "12px",
    fontWeight: 800,
    marginBottom: "8px",
  },
  statValue: {
    color: "#00ff99",
    fontSize: "28px",
    fontWeight: 900,
  },
  toolbar: {
    marginBottom: "16px",
  },
  search: {
    width: "100%",
    maxWidth: "520px",
    background: "#050b14",
    border: "1px solid rgba(0,242,255,0.35)",
    color: "#fff",
    borderRadius: "14px",
    padding: "13px 16px",
    outline: "none",
    fontWeight: 700,
  },
  card: {
    background: "rgba(7, 17, 31, 0.96)",
    border: "1px solid rgba(0,242,255,0.28)",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    background: "#020617",
    color: "#b8faff",
    padding: "15px",
    fontSize: "13px",
    textAlign: "left",
    borderBottom: "1px solid rgba(0,242,255,0.2)",
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  td: {
    padding: "14px 15px",
    color: "#e5f7ff",
    fontSize: "13px",
  },
  tdEmail: {
    padding: "14px 15px",
    color: "#fff",
    fontWeight: 800,
    fontSize: "13px",
  },
  tdAmount: {
    padding: "14px 15px",
    color: "#00ff99",
    fontWeight: 900,
    fontSize: "14px",
  },
  tdMuted: {
    padding: "14px 15px",
    color: "#8fa2bd",
    fontSize: "13px",
  },
  badge: {
    display: "inline-block",
    background: "rgba(0,242,255,0.1)",
    color: "#00f2ff",
    border: "1px solid rgba(0,242,255,0.28)",
    padding: "5px 10px",
    borderRadius: "999px",
    fontWeight: 900,
    fontSize: "12px",
  },
  empty: {
    padding: "28px",
    color: "#8fa2bd",
    textAlign: "center",
    fontWeight: 800,
  },
};